import React, { useState } from 'react';
import {
  authorizingAttempts,
  canConfirmEfficacy,
  efficacyAttempts,
  evaluateIssueClosure,
  governanceState,
  GOVERNANCE_LABELS,
  latestEfficacyAttempt,
  latestReopenAt,
  operationalSupport,
  readyForEfficacyCheck,
  verificationDueAt,
  type CountermeasureRef,
} from '../../utils/efficacyCheck';
import { getIssueOrigin } from '../../utils/issueOrigin';
import { efficacySurface } from '../../utils/problemWorkspaceView';
import type { Issue } from '../../types/issue';
import { getStatusTransitions, VALIDATION_MODE_LABELS } from '../../types/issue';

interface EfficacyVerificationPanelProps {
  issue: Issue;
  actions: Array<CountermeasureRef & { title?: string; completed_at?: string | null }>;
  members: Array<{ id: string; name: string }>;
  actorId?: string;
  actorRole?: string | null;
  embedded?: boolean;
  showResolutionNote?: boolean;
  onConfirm: (note: string) => Promise<void>;
  onReject: (note: string) => Promise<void>;
  onCreateAction: () => void;
  onSetVerifier: (userId: string | null) => Promise<void>;
  onSetExplicitDue: (isoDate: string | null) => Promise<void>;
}

export const EfficacyVerificationPanel: React.FC<EfficacyVerificationPanelProps> = ({
  issue,
  actions,
  members,
  actorId,
  actorRole,
  embedded = false,
  showResolutionNote = true,
  onConfirm,
  onReject,
  onCreateAction,
  onSetVerifier,
  onSetExplicitDue,
}) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = readyForEfficacyCheck(actions, issue.status);
  const attempts = efficacyAttempts(issue.metadata);
  const reopenAt = latestReopenAt(issue.metadata);
  const authorizing = authorizingAttempts(attempts, reopenAt);
  const closure = evaluateIssueClosure({
    actions,
    attempts,
    closureReason: issue.metadata?.closure_reason,
    metadata: issue.metadata,
  });
  const latest = latestEfficacyAttempt(attempts);
  const closeTransition = (getStatusTransitions(issue.mode)[issue.status] ?? []).includes('closed');
  const originView = getIssueOrigin({
    organizationId: issue.organization_id,
    source: issue.source,
    projectId: issue.project_id,
    metadata: issue.metadata,
  });
  const support = operationalSupport(issue.metadata);
  const windowDays = issue.validation_config?.mode === 'time'
    ? issue.validation_config.time?.validation_days
    : null;
  const dueAt = verificationDueAt({
    validationMode: issue.validation_config?.mode,
    windowDays,
    explicitDueAt: issue.metadata?.verification_due_at,
    actions,
  });
  const state = governanceState({
    status: issue.status,
    actions,
    attempts: authorizing,
    dueAt,
  });
  const permission = canConfirmEfficacy({
    severity: issue.severity,
    verifierId: issue.metadata?.verification_owner_id,
    problemOwnerId: issue.assigned_to,
    actorId,
    actorRole,
  });
  const verifierName = members.find((member) => member.id === issue.metadata?.verification_owner_id)?.name;
  const openCount = actions.filter((action) => action.status !== 'completed' && action.status !== 'cancelled').length;
  const done = actions.filter((action) => action.status === 'completed');

  async function run(action: (value: string) => Promise<void>) {
    setError(null);
    if (!note.trim()) {
      setError('A observação é obrigatória. A conclusão da ação não encerra o problema.');
      return;
    }
    setBusy(true);
    try {
      await action(note.trim());
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar a verificação');
    } finally {
      setBusy(false);
    }
  }

  const completedCount = actions.filter((action) => action.status === 'completed').length;
  if (efficacySurface({ completedCount, attemptCount: attempts.length }) === 'empty') {
    return (
      <section className={embedded ? 'text-slate-700 dark:text-slate-200' : 'shrink-0 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900'}>
        <p className="text-[13px] font-medium text-slate-800 dark:text-slate-100">
          Nenhuma contramedida vinculada
        </p>
        <p className="text-[12px] text-slate-500">
          Sem contramedida concluída, não há resultado a verificar.
        </p>
        {issue.status !== 'closed' ? (
          <button
            type="button"
            onClick={onCreateAction}
            className="mt-1.5 rounded border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Nova contramedida
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <section className={embedded ? 'text-slate-700 dark:text-slate-200' : 'shrink-0 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900'}>
      {embedded ? null : (
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Verificação de eficácia</p>
      )}
      <p className={`text-[13px] font-semibold ${state === 'verificacao_vencida' || state === 'nao_eficaz' ? 'text-amber-800 dark:text-amber-300' : 'text-slate-900 dark:text-slate-100'}`}>
        {GOVERNANCE_LABELS[state]}
      </p>
      {showResolutionNote ? (
        <p className="mt-1 text-[12px] text-slate-600 dark:text-slate-300">
          Ação concluída não significa problema resolvido.
        </p>
      ) : null}

      <div className="mt-2 grid gap-2 text-[12px] text-slate-600 dark:text-slate-300 sm:grid-cols-2">
        {embedded ? null : (
          <p><span className="font-semibold text-slate-800 dark:text-slate-100">Problema. </span>{issue.title}</p>
        )}
        {embedded ? null : (
          <p>
            <span className="font-semibold text-slate-800 dark:text-slate-100">Origem. </span>
            {originView.label}
          </p>
        )}
        <p>
          <span className="font-semibold text-slate-800 dark:text-slate-100">Contramedidas concluídas. </span>
          {done.length === 0 ? 'Nenhuma concluída' : `${done.length}`}
        </p>
        {done.map((action) => {
          const covered = closure.coveredActionIds.includes(action.id);
          return (
            <p key={action.id}>
              <span className="font-semibold text-slate-800 dark:text-slate-100">{action.title || 'Contramedida'}. </span>
              {covered ? 'Coberta por eficácia válida' : 'Ainda precisa de verificação'}
            </p>
          );
        })}
        <p>
          <span className="font-semibold text-slate-800 dark:text-slate-100">Responsável pela verificação. </span>
          {verifierName ?? (issue.severity === 'critical' ? 'Obrigatório neste problema crítico' : 'Opcional. Sem ele, confirma o responsável pelo problema ou um perfil gerencial.')}
        </p>
        <p>
          <span className="font-semibold text-slate-800 dark:text-slate-100">Critério. </span>
          {issue.validation_config?.mode
            ? VALIDATION_MODE_LABELS[issue.validation_config.mode]
            : 'Sem critério configurado'}
          {' '}O critério não decide a eficácia.
        </p>
        {latest ? (
          <p>
            <span className="font-semibold text-slate-800 dark:text-slate-100">Última decisão. </span>
            {latest.result === 'effective' ? 'Eficaz' : 'Não eficaz'}
            {' · '}
            {latest.actor_name || 'Usuário'}
            {' · '}
            {new Date(latest.at).toLocaleString('pt-BR')}
          </p>
        ) : null}
        <p>
          <span className="font-semibold text-slate-800 dark:text-slate-100">Janela. </span>
          {windowDays != null
            ? `${windowDays} dias após a última contramedida concluída${dueAt ? ` · prazo ${new Date(dueAt).toLocaleDateString('pt-BR')}` : ''}`
            : dueAt
              ? `Data explícita ${new Date(dueAt).toLocaleDateString('pt-BR')}`
              : 'Sem janela configurada'}
        </p>
        {support.openingWip != null ? (
          <p>
            <span className="font-semibold text-slate-800 dark:text-slate-100">Na abertura. </span>
            WIP {support.openingWip}. Sem leitura atual confiável, não há comparação e isso não confirma eficácia.
          </p>
        ) : null}
      </div>

      {attempts.length > 0 ? (
        <ol className="mt-2 space-y-1">
          {attempts.map((attempt) => (
            <li key={`${attempt.at}-${attempt.result}`} className="text-[11px] text-slate-600 dark:text-slate-300">
              {attempt.result === 'effective' ? 'Eficaz' : 'Não eficaz'}
              {' · '}
              {attempt.actor_name || 'Usuário'}
              {' · '}
              {attempt.note}
              {attempt.action_ids.length > 0 ? ` · ações ${attempt.action_ids.join(', ')}` : ''}
            </li>
          ))}
        </ol>
      ) : null}

      {reopenAt ? (
        <p className="mt-2 text-[11px] text-slate-600 dark:text-slate-300">
          Tentativas anteriores à reabertura permanecem no histórico e não autorizam o próximo encerramento.
        </p>
      ) : null}

      {issue.status !== 'closed' && !embedded ? (
        <p className="mt-2 text-[12px] text-slate-700 dark:text-slate-200">
          {closure.canClose
            ? (closeTransition
              ? 'A regra de encerramento está satisfeita. O problema continua aberto até alguém encerrar.'
              : 'A regra de encerramento está satisfeita. Esta etapa ainda não oferece o encerramento.')
            : closure.reason}
        </p>
      ) : null}

      {issue.status === 'closed' ? (
        <p className="mt-2 text-[12px] font-semibold text-emerald-700 dark:text-emerald-400">
          {attempts.some((attempt) => attempt.result === 'effective')
            ? 'Problema encerrado depois de uma confirmação de eficácia.'
            : issue.metadata?.closure_reason
              ? `Encerrado sem contramedida. Motivo: ${issue.metadata.closure_reason}`
              : 'Problema encerrado.'}
        </p>
      ) : ready ? (
        <div className="mt-3">
          <p className="text-[12px] font-semibold text-slate-900 dark:text-slate-100">
            {state === 'verificacao_vencida'
              ? 'O prazo da verificação passou. O problema continua aberto. Isso é atraso de verificação, não falha da contramedida.'
              : 'Contramedidas executadas. Próximo passo: verificar eficácia.'}
          </p>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="O que foi observado?"
            className="mt-2 w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-[12px] dark:border-slate-700 dark:bg-slate-950"
            rows={2}
          />
          <label className="mt-2 block text-[11px] text-slate-600 dark:text-slate-300">
            Responsável pela verificação
            <select
              value={issue.metadata?.verification_owner_id ?? ''}
              onChange={(event) => void onSetVerifier(event.target.value || null)}
              className="mt-1 block w-full rounded border border-slate-200 bg-white px-2 py-1 text-[12px] dark:border-slate-700 dark:bg-slate-950"
            >
              <option value="">{issue.severity === 'critical' ? 'Selecione o verificador' : 'Sem verificador específico'}</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          </label>
          {issue.validation_config?.mode !== 'time' ? (
            <label className="mt-2 block text-[11px] text-slate-600 dark:text-slate-300">
              Data explícita de verificação
              <input
                type="date"
                value={issue.metadata?.verification_due_at?.slice(0, 10) ?? ''}
                onChange={(event) => void onSetExplicitDue(event.target.value ? `${event.target.value}T23:59:59.000Z` : null)}
                className="mt-1 block rounded border border-slate-200 bg-white px-2 py-1 text-[12px] dark:border-slate-700 dark:bg-slate-950"
              />
            </label>
          ) : null}
          {error ? <p className="mt-1 text-[11px] text-red-600">{error}</p> : null}
          {permission.allowed === false ? <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">{permission.reason}</p> : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !permission.allowed}
              onClick={() => void run(onConfirm)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-60"
            >
              Confirmar eficácia
            </button>
            <button
              type="button"
              disabled={busy || !permission.allowed}
              onClick={() => void run(onReject)}
              className="rounded border border-amber-500 px-3 py-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300"
            >
              Rejeitar eficácia
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-[12px] text-slate-600 dark:text-slate-300">
          {openCount > 0
            ? `${openCount} contramedida(s) ainda aberta(s). A verificação fica para quando todas forem executadas.`
            : actions.some((action) => action.status === 'cancelled')
              ? 'Contramedida cancelada não exige verificação de eficácia.'
              : 'Ainda não há contramedida vinculada.'}
        </p>
      )}

      {attempts.some((attempt) => attempt.result === 'ineffective') && issue.status !== 'closed' && !embedded ? (
        <button
          type="button"
          onClick={onCreateAction}
          className="mt-2 text-[11px] font-semibold text-[var(--nav-active-bar)]"
        >
          Nova ação
        </button>
      ) : null}
    </section>
  );
};
